import React from 'react';
import { View } from 'react-native';

import { Dialog, ErrorCallout, Heading, Spinner, Text } from '../../components';
import { PrimaryButton } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import WarehousePrintJobStatus from '../picklist/WarehousePrintJobStatus';

/**
 * The picker's print dialogs: the packing / repair-only label sent from a
 * label-only row, and the completed pick's tub sticker. Props are the
 * `labelDialog` and `completedPickDialog` objects from usePickerMyDay.
 */
const PickerLabelDialogs = ({ labelDialog, completedPickDialog }) => {
	const styles = useThemedStyles(createStyles);

	return (
		<>
			<Dialog
				isOpen={labelDialog.isOpen}
				onClose={labelDialog.onClose}
			>
				<View style={styles.content}>
					<Heading
						heading={labelDialog.labelName}
						subHeading={labelDialog.machineName}
					/>
					{
						labelDialog.isSubmitting && !labelDialog.job && <Spinner />
					}
					{Boolean(labelDialog.error) && !labelDialog.isSubmitting && (
						<>
							<ErrorCallout text={`The label was not sent to the warehouse printer. ${labelDialog.error}`} />
							{!labelDialog.job && (
								<PrimaryButton label="Try again" icon="printer" size="compact" onPress={labelDialog.onRetry} />
							)}
						</>
					)}
					<WarehousePrintJobStatus
						job={labelDialog.job}
						labelName={labelDialog.labelName}
						onReprint={labelDialog.onReprint}
						isSubmitting={labelDialog.isSubmitting}
					/>
					<Text style={styles.note}>
						You can close this window after the job is queued. The label will stay in the warehouse queue.
					</Text>
				</View>
			</Dialog>
			<Dialog
				isOpen={completedPickDialog.isOpen}
				onClose={completedPickDialog.onClose}
			>
				<View style={styles.content}>
					<Heading
						heading="Completed pick"
						subHeading={completedPickDialog.machineName}
					/>
					{completedPickDialog.isLoading && !completedPickDialog.job && <Spinner />}
					{Boolean(completedPickDialog.error) && !completedPickDialog.isSubmitting && (
						<ErrorCallout text={`The tub sticker was not sent to the warehouse printer. ${completedPickDialog.error}`} />
					)}
					{!completedPickDialog.isLoading && !completedPickDialog.job && (
						<>
							<Text style={styles.note}>
								No tub sticker print is recorded for this completed pick. You can send one now.
							</Text>
							<PrimaryButton
								label="Print tub sticker"
								icon="printer"
								size="compact"
								onPress={completedPickDialog.onPrint}
								loading={completedPickDialog.isSubmitting}
							/>
						</>
					)}
					<WarehousePrintJobStatus
						job={completedPickDialog.job}
						labelName="Tub sticker"
						onReprint={completedPickDialog.onReprint}
						isSubmitting={completedPickDialog.isSubmitting}
					/>
					<Text style={styles.note}>
						This completed pick cannot be reopened. Reprinting only sends another tub sticker to the warehouse queue.
					</Text>
				</View>
			</Dialog>
		</>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	content: {
		gap: space.md,
	},
	note: {
		...type.secondary,
		color: colors.textSecondary,
	},
});

export default PickerLabelDialogs;
